"use client";

import { c as _c } from "react/compiler-runtime";
import ActionNode from "@/app/(v2)/workflows/_components/ActionNode";
import TriggerNode from "@/app/(v2)/workflows/_components/TriggerNode";
import WorkflowEdge from "@/app/(v2)/workflows/_components/WorkflowEdge";
import { Handle, Position, useNodes, useNodesInitialized, useReactFlow, type Node } from "@xyflow/react";
import { useEffect, useMemo, useRef } from "react";

// Marketing Placeholder Node Component
function MarketingPlaceholder(t0) {
  const $ = _c(10);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data.showTop) {
    t1 = data.showTop && <Handle type="target" position={Position.Top} className="!border-ds-neutral-300 !bg-ds-surface-card !h-3 !w-3 !border-2 dark:!border-zinc-600" />;
    $[0] = data.showTop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== data.text) {
    t2 = <div className="border-ds-neutral-300 dark:border-ds-neutral-700 dark:bg-ds-neutral-900/50 flex w-[280px] items-center justify-center gap-2 rounded-lg border-2 border-dashed bg-white/50 px-4 py-3"><span className="text-ds-text-muted text-xs font-medium">{data.text}</span></div>;
    $[2] = data.text;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== data.showBottom) {
    t3 = data.showBottom && <Handle type="source" position={Position.Bottom} className="!border-ds-neutral-300 !bg-ds-surface-card !h-3 !w-3 !border-2 dark:!border-zinc-600" />;
    $[4] = data.showBottom;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <div className="relative">{t1}{t2}{t3}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

// Component to dynamically adjust node positions based on measured heights
export function DynamicNodePositioning(t0) {
  const $ = _c(30);
  const {
    isTrigger
  } = t0;
  const nodes = useNodes();
  const nodesInitialized = useNodesInitialized();
  const {
    setNodes
  } = useReactFlow();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const lastCommittedPositionsRef = useRef(t1);
  let t2;
  let t3;
  if ($[1] !== nodes) {
    t3 = nodes.find(_temp);
    $[1] = nodes;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  t2 = t3;
  const triggerNode = t2;
  let t4;
  let t5;
  if ($[3] !== nodes) {
    t5 = nodes.find(_temp2);
    $[3] = nodes;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  t4 = t5;
  const placeholderAction = t4;
  let t6;
  let t7;
  if ($[5] !== nodes) {
    t7 = nodes.find(_temp3);
    $[5] = nodes;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  t6 = t7;
  const topPlaceholder = t6;
  let t8;
  let t9;
  if ($[7] !== nodes) {
    t9 = nodes.find(_temp4);
    $[7] = nodes;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  t8 = t9;
  const bottomPlaceholder = t8;
  const triggerNodeHeight = triggerNode?.measured?.height;
  const topPlaceholderHeight = topPlaceholder?.measured?.height;
  let t10;
  if ($[9] !== bottomPlaceholder || $[10] !== isTrigger || $[11] !== nodes.length || $[12] !== nodesInitialized || $[13] !== placeholderAction || $[14] !== setNodes || $[15] !== topPlaceholderHeight || $[16] !== triggerNode || $[17] !== triggerNodeHeight) {
    t10 = () => {
      if (!nodesInitialized || nodes.length === 0) {
        return;
      }
      const updates = [];
      if (isTrigger) {
        if (triggerNodeHeight && placeholderAction) {
          const expectedPlaceholderY = triggerNodeHeight + 35;
          const lastY = lastCommittedPositionsRef.current["placeholder-action"];
          if (lastY === undefined || Math.abs(lastY - expectedPlaceholderY) > 1) {
            updates.push({
              id: "placeholder-action",
              y: expectedPlaceholderY
            });
          }
        }
      } else {
        if (topPlaceholderHeight && triggerNode) {
          const expectedActionY = topPlaceholderHeight + 35;
          const lastActionY = lastCommittedPositionsRef.current["integration-preview"];
          if (lastActionY === undefined || Math.abs(lastActionY - expectedActionY) > 1) {
            updates.push({
              id: "integration-preview",
              y: expectedActionY
            });
          }
          if (triggerNodeHeight && bottomPlaceholder) {
            const expectedBottomY = expectedActionY + triggerNodeHeight + 35;
            const lastBottomY = lastCommittedPositionsRef.current["placeholder-action-bottom"];
            if (lastBottomY === undefined || Math.abs(lastBottomY - expectedBottomY) > 1) {
              updates.push({
                id: "placeholder-action-bottom",
                y: expectedBottomY
              });
            }
          }
        }
      }
      if (updates.length > 0) {
        updates.forEach(t11 => {
          const {
            id,
            y
          } = t11;
          lastCommittedPositionsRef.current[id] = y;
        });
        setNodes(nds => nds.map(n_3 => {
          const update = updates.find(u => u.id === n_3.id);
          return update ? {
            ...n_3,
            position: {
              ...n_3.position,
              y: update.y
            }
          } : n_3;
        }));
      }
    };
    $[9] = bottomPlaceholder;
    $[10] = isTrigger;
    $[11] = nodes.length;
    $[12] = nodesInitialized;
    $[13] = placeholderAction;
    $[14] = setNodes;
    $[15] = topPlaceholderHeight;
    $[16] = triggerNode;
    $[17] = triggerNodeHeight;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== bottomPlaceholder || $[20] !== isTrigger || $[21] !== nodes.length || $[22] !== nodesInitialized || $[23] !== placeholderAction || $[24] !== setNodes || $[25] !== topPlaceholder || $[26] !== topPlaceholderHeight || $[27] !== triggerNode || $[28] !== triggerNodeHeight) {
    t11 = [nodes.length, nodesInitialized, isTrigger, setNodes, triggerNodeHeight, topPlaceholderHeight, triggerNode, placeholderAction, topPlaceholder, bottomPlaceholder];
    $[19] = bottomPlaceholder;
    $[20] = isTrigger;
    $[21] = nodes.length;
    $[22] = nodesInitialized;
    $[23] = placeholderAction;
    $[24] = setNodes;
    $[25] = topPlaceholder;
    $[26] = topPlaceholderHeight;
    $[27] = triggerNode;
    $[28] = triggerNodeHeight;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  useEffect(t10, t11);
  return null;
}

// Component to handle responsive fit view
function _temp4(n_2) {
  return n_2.id === "placeholder-action-bottom";
}
function _temp3(n_1) {
  return n_1.id === "placeholder-trigger-top";
}
function _temp2(n_0) {
  return n_0.id === "placeholder-action";
}
function _temp(n) {
  return n.id === "integration-preview";
}
export function ResponsiveFitView(t0) {
  const $ = _c(8);
  const {
    nodes
  } = t0;
  const {
    fitView
  } = useReactFlow();
  let t1;
  let t2;
  if ($[0] !== fitView) {
    t1 = () => {
      const initialTimer = setTimeout(() => {
        fitView({
          duration: 200,
          padding: 0.3,
          minZoom: 0.5,
          maxZoom: 1
        });
      }, 100);
      const container = document.querySelector(".integration-preview-container");
      if (!container) {
        return () => clearTimeout(initialTimer);
      }
      let resizeTimer;
      const resizeObserver = new ResizeObserver(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          fitView({
            duration: 200,
            padding: 0.3,
            minZoom: 0.5,
            maxZoom: 1
          });
        }, 100);
      });
      resizeObserver.observe(container);
      return () => {
        clearTimeout(initialTimer);
        clearTimeout(resizeTimer);
        resizeObserver.disconnect();
      };
    };
    t2 = [fitView];
    $[0] = fitView;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== fitView) {
    t3 = () => {
      const timer = setTimeout(() => {
        fitView({
          duration: 300,
          padding: 0.3,
          minZoom: 0.5,
          maxZoom: 1
        });
      }, 50);
      return () => clearTimeout(timer);
    };
    $[3] = fitView;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== fitView || $[6] !== nodes) {
    t4 = [nodes, fitView];
    $[5] = fitView;
    $[6] = nodes;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(t3, t4);
  return null;
}

// React Flow node types
export const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  placeholder: MarketingPlaceholder
};

// React Flow edge types
export const edgeTypes = {
  workflow: WorkflowEdge
};